import angular, { IPromise } from 'angular'
import {
  deleteDB,
  openDB,
  IDBPDatabase,
  IDBPTransaction,
  StoreNames,
  StoreValue,
  IndexNames,
  IndexKey,
} from 'idb'
import { trackError } from '@vend/vend.js'

import { $q } from '../../ng-compat/imports'
import { WebRegisterDb, Cursor } from './database-types'

type Iterator<
  Store extends StoreNames<WebRegisterDb>,
  Mode extends IDBTransactionMode
> = (cursor: Cursor<Store, Mode>) => boolean

const iterateCursor = <
  Store extends StoreNames<WebRegisterDb>,
  Mode extends IDBTransactionMode
>(
  iterator: Iterator<Store, Mode>
) => {
  const loop = (
    cursor: Cursor<Store, Mode> | null
  ): Promise<Cursor<Store, Mode> | undefined> | undefined => {
    if (cursor && iterator(cursor)) {
      return cursor.continue().then(loop)
    }
  }
  return loop
}

/**
 * A treo-like interface to idb object store.
 * It meant we could make fewer code changes when moving to idb.
 */
export class Store<StoreName extends StoreNames<WebRegisterDb>> {
  constructor(
    private readonly db: Promise<IDBPDatabase<WebRegisterDb>>,
    private readonly storeName: StoreName
  ) {}

  public index<IndexName extends IndexNames<WebRegisterDb, StoreName>>(
    indexName: IndexName
  ) {
    return new Index(this.db, this.storeName, indexName)
  }

  public get(key: string) {
    return this.db.then(db => {
      const request = db.get(this.storeName, key)
      request.catch(this.trackError)
      return request
    })
  }

  public all() {
    // Don't use getAll, as it requires Safari 10.1+
    // and is not supported by non-Chromium Edge at all.
    const allValues: Array<StoreValue<WebRegisterDb, StoreName>> = []
    const allIterator = (cursor: Cursor<StoreName>) => {
      allValues.push(cursor.value)
      return true
    }
    const iterate = iterateCursor(allIterator)
    return this.db
      .then(db => {
        const request = db.transaction(this.storeName).store.openCursor()
        request.catch(this.trackError)
        return request
      })
      .then(cursor => iterate(cursor))
      .then(() => allValues)
  }

  public transformAll(callback: (value: any) => {}) {
    const allIterator = (cursor: Cursor<StoreName, 'readwrite'>) => {
      cursor.update(callback(cursor.value))
      return true
    }
    const iterate = iterateCursor(allIterator)
    return this.db
      .then(db => {
        const request = db
          .transaction(this.storeName, 'readwrite')
          .store.openCursor()
        request.catch(this.trackError)
        return request
      })
      .then(cursor => iterate(cursor))
  }

  public clear() {
    return this.db.then(db => {
      const request = db.clear(this.storeName)
      request.catch(this.trackError)
      return request
    })
  }

  public put(
    keyOrValue: string | StoreValue<WebRegisterDb, StoreName>,
    value?: StoreValue<WebRegisterDb, StoreName>
  ) {
    let key = keyOrValue
    if (value === undefined) {
      value = keyOrValue
      key = undefined
    }
    return this.db.then(db => {
      const request = db.put(this.storeName, value, key)
      request.catch(this.trackError)
      return request
    })
  }

  public del(key: string) {
    return this.db.then(db => {
      const request = db.delete(this.storeName, key)
      request.catch(this.trackError)
      return request
    })
  }

  public batch(
    update:
      | {
          [key: string]: StoreValue<WebRegisterDb, StoreName> | null
        }
      | Array<StoreValue<WebRegisterDb, StoreName>>
  ) {
    return this.db.then(db => {
      const tx = db.transaction(this.storeName, 'readwrite')
      const store = tx.store
      if (Array.isArray(update)) {
        for (const value of update) {
          const request = store.put(value)
          request.catch(this.trackError)
        }
      } else {
        for (const key of Object.keys(update)) {
          let request: Promise<unknown>
          const value = update[key]
          if (value === null) {
            request = store.delete(key)
          } else {
            if (store.keyPath) {
              request = store.put(value)
            } else {
              request = store.put(value, key)
            }
          }
          request.catch(this.trackError)
        }
      }
      return tx.done
    })
  }

  public count() {
    return this.db.then(db => {
      const request = db.count(this.storeName)
      request.catch(this.trackError)
      return request
    })
  }

  public cursor({
    range,
    iterator,
  }: {
    range?: string
    iterator: Iterator<StoreName, 'readonly'>
  }) {
    return this.db
      .then(db => {
        const request = db.transaction(this.storeName).store.openCursor(range)
        request.catch(this.trackError)
        return request
      })
      .then(iterateCursor(iterator))
  }

  /**
   * Database operation like put, delete, openCursor might throw exception
   * Send those exception to error tracker to help troubleshooting and also to avoid Unhandled promise rejection
   * @param {any} [reason=Unknown reason] - Reason the promise rejected
   */
  public trackError(reason: any = 'Unknown reason') {
    trackError(reason)
  }
}

/**
 * A treo-like interface to idb object store index.
 * It meant we could make fewer code changes when moving to idb.
 */
export class Index<
  StoreName extends StoreNames<WebRegisterDb>,
  IndexName extends IndexNames<WebRegisterDb, StoreName>
> {
  constructor(
    private readonly db: IPromise<IDBPDatabase<WebRegisterDb>>,
    private readonly storeName: StoreName,
    private readonly indexName: IndexName
  ) {}

  public get(key: IndexKey<WebRegisterDb, StoreName, IndexName>) {
    return this.db.then(db => {
      const request = db.getFromIndex(this.storeName, this.indexName, key)
      request.catch(this.trackError)
      return request
    })
  }

  public getAll(key: IndexKey<WebRegisterDb, StoreName, IndexName>) {
    // Don't use getAllFromIndex, as it requires Safari 10.1+
    // and is not supported by non-Chromium Edge at all.
    const allValues: Array<StoreValue<WebRegisterDb, StoreName>> = []
    const allIterator = (cursor: Cursor<StoreName>) => {
      allValues.push(cursor.value)
      return true
    }
    const iterate = iterateCursor(allIterator)
    return this.db
      .then(db => {
        const request = db
          .transaction(this.storeName)
          .store.index(this.indexName)
          .openCursor(key)
        request.catch(this.trackError)
        return request
      })
      .then(cursor => iterate(cursor))
      .then(() => allValues)
  }

  public count(key?: IndexKey<WebRegisterDb, StoreName, IndexName>) {
    return this.db.then(db => {
      const request = db.countFromIndex(this.storeName, this.indexName, key)
      request.catch(this.trackError)
      return request
    })
  }

  public cursor({
    range,
    iterator,
  }: {
    range?: IndexKey<WebRegisterDb, StoreName, IndexName>
    iterator: (cursor: Cursor<StoreName>) => boolean
  }) {
    return this.db
      .then(db => {
        const request = db
          .transaction(this.storeName)
          .store.index(this.indexName)
          .openCursor(range)
        request.catch(this.trackError)
        return request
      })
      .then(iterateCursor(iterator))
  }

  /**
   * Database operation like put, delete, openCursor might throw exception
   * Send those exception to error tracker to help troubleshooting and also to avoid Unhandled promise rejection
   * @param reason: any
   */
  public trackError(reason: any) {
    trackError('[Index]', reason)
  }
}

function upgradeDb(
  db: IDBPDatabase,
  oldVersion: number,
  tx: IDBPTransaction<unknown, string[], 'versionchange'>
): void {
  const store = (name: string) => tx.objectStore(name)

  // All fallthrough in this switch is intentional.
  switch (oldVersion) {
    case 0:
      // @todo need to find a better place/way to document schemas

      //  {
      //      id: '<uuid>',
      //      name: 'Awesome box of Lego',
      //      handle: 'awesomelego',
      //      sku: 109833,
      //      description: 'A really awesome box of Lego',
      //      active: true,
      //      deleted_at: null
      //      // and lots more!!
      //  }
      db.createObjectStore('products', { keyPath: 'id' }).createIndex(
        'byVariantParentId',
        'variant_parent_id'
      )

      //  {
      //      id: '<uuid>',
      //      name: 'GST',
      //      rates: [{
      //          id: '<uuid>',
      //          name: 'GST',
      //          rate: 0.15
      //      }],
      //      deleted_at: null
      //  }
      db.createObjectStore('taxes', { keyPath: 'id' })

      //  {
      //      id: '<uuid>',
      //      name: 'Our Outlet Store',
      //      default_tax_id: '<uuid>',
      //      deleted_at: null
      //  }
      db.createObjectStore('outlets', { keyPath: 'id' })

      //  {
      //      outlet_id: '<uuid>',
      //      product_id: '<uuid>',
      //      tax_id: '<uuid>',
      //      deleted_at: null
      //  }
      db.createObjectStore('productTaxes', { keyPath: 'product_id' })

      //  {
      //      entityStoreName: 'priceBooks',
      //      entityName: 'price_books',
      //      lastSyncedVersion: 312,
      //      maxVersion: 376,
      //      initialSyncHasOccurred: true
      //  }
      db.createObjectStore('versions', { keyPath: 'entityStoreName' })

      //  {
      //      id: '<uuid>',
      //      name: 'General Price Book (All Products)',
      //      type: 'GENERAL',
      //      valid_from: "2014-11-25 00:00:00",
      //      valid_to: "2014-12-02 00:00:00",
      //      outlet_id: '<uuid>',
      //      customer_group_id: '<uuid>'
      //  }
      db.createObjectStore('priceBooks', { keyPath: 'id' })

      //  {
      //      id: '<uuid>',
      //      product_id: '<uuid>',
      //      pricebook_id: '<uuid>',
      //      min_units: '1.00000',
      //      max_units: '3.00000',
      //      loyalty_value: 5,
      //      price: '12.50'
      //  }
      db.createObjectStore('priceBookProducts', {
        keyPath: 'id',
      }).createIndex('byProductId', 'product_id')

      //  POS_STATE: {
      //      outletId: '<uuid>',
      //      registerId: '<uuid>',
      //      activeSaleLocalId: '<uuid>',
      //      // etc
      //  },
      //  RETAILER_SETTINGS: {
      //  }
      db.createObjectStore('settings')

      //  {
      //      localId: '<uuid>',
      //      id: '<uuid>',
      //      lineItems: [],
      //      totals: {
      //          exclusivePrice: '12.00',
      //          tax: '3.00',
      //          inclusivePrice: '15.00'
      //      }
      //      // etc
      //  }
      db.createObjectStore('sales', { keyPath: 'localId' })

      //  {
      //      id: '<uuid>',
      //      name: 'Cash',
      //      type_id: 1,
      //      config: {},
      //      deleted_at: null
      //  }
      db.createObjectStore('paymentTypes', { keyPath: 'id' })

      //  {
      //      id: '<uuid>'
      //  }
      db.createObjectStore('registers', { keyPath: 'id' })

      //  {
      //      id: '<uuid>'
      //  }
      db.createObjectStore('users', { keyPath: 'id' })

    // eslint-disable-next-line no-fallthrough
    case 1:
      //  {
      //      id: '<uuid>',
      //      customer_group_id: '<uuid>',
      //      first_name: 'Darth',
      //      last_name: 'Vader'
      //      // etc
      //  }
      db.createObjectStore('customers', { keyPath: 'id' })

      //  {
      //      id: '<uuid>',
      //      group_id: '1111',
      //      name: 'All Customers',
      //  }
      db.createObjectStore('customerGroups', { keyPath: 'id' })

    // eslint-disable-next-line no-fallthrough
    case 2:
      //  {
      //      id: '<uuid>',
      //      name: 'Nike',
      //  }
      db.createObjectStore('brands', { keyPath: 'id' })

      //  {
      //      id: '<uuid>',
      //      name: 'The Warehouse',
      //  }
      db.createObjectStore('suppliers', { keyPath: 'id' })

      //  {
      //      id: '<uuid>',
      //      name: 'hat',
      //  }
      db.createObjectStore('tags', { keyPath: 'id' })

      //  {
      //      id: '<uuid>',
      //      name: 'Hats',
      //  }
      db.createObjectStore('productTypes', { keyPath: 'id' })

    // eslint-disable-next-line no-fallthrough
    case 3:
      //  {
      //      id: '<uuid>',
      //      receipt: {
      //          fields: {
      //               label_invoice: 'invoice no. prefix label',
      //               label_invoice_title: 'invoice heading label',
      //               label_served_by: 'Served by:',
      //               label_line_discount: 'Discount label',
      //               label_sub_total: 'Subtotal label',
      //               label_tax: 'Tax label',
      //               label_to_pay: 'To pay label',
      //               label_total: 'Total label',
      //               label_change: 'Change label',
      //               header: '<h1>HEADER</h1>',
      //               footer: '<h1>FOOTER</h1>'
      //          }
      //      },
      //  }
      db.createObjectStore('legacyRegisters', { keyPath: 'id' })

    // eslint-disable-next-line no-fallthrough
    case 4:
      // Index for looking up sales by their server generated ID
      store('sales').createIndex('byId', 'id', { unique: true })

    // eslint-disable-next-line no-fallthrough
    case 5:
      store('sales').createIndex('syncStatus', 'syncStatus')

    // eslint-disable-next-line no-fallthrough
    case 6:
      store('products').createIndex('byVersion', 'version', { unique: true })
      store('customers').createIndex('byVersion', 'version', { unique: true })

    // eslint-disable-next-line no-fallthrough
    case 7:
      db.createObjectStore('quickKeyLayouts', { keyPath: 'id' })

    // eslint-disable-next-line no-fallthrough
    case 8:
      store('products').createIndex('bySku', 'sku')

    // eslint-disable-next-line no-fallthrough
    case 9:
      store('customers').createIndex('byCustomerCode', 'customer_code')

    // eslint-disable-next-line no-fallthrough
    case 10:
      db.createObjectStore('receiptTemplates', { keyPath: 'id' })

    // eslint-disable-next-line no-fallthrough
    case 11:
      // Index for retrieving deleted products. Although deleted products arent synced, they are temporarily required
      // when dealing with sales which contain them. This index facilitates their easy removal.
      store('products').createIndex('byDeletedAt', 'deleted_at')

    // eslint-disable-next-line no-fallthrough
    case 12:
      db.deleteObjectStore('legacyRegisters')

    // eslint-disable-next-line no-fallthrough
    case 13:
      db.createObjectStore('receiptTemplateTypes', { keyPath: 'name' })
      db.createObjectStore('receiptSettings', { keyPath: 'id' })

    // eslint-disable-next-line no-fallthrough
    case 14:
      db.createObjectStore('channels', { keyPath: 'id' }).createIndex(
        'byChannelType',
        'channel_type'
      )

    // eslint-disable-next-line no-fallthrough
    case 15:
      store('products').createIndex('byProductCode', 'product_codes_array', {
        multiEntry: true,
      })

    // eslint-disable-next-line no-fallthrough
    case 16:
      // {
      //   outlet_id: '<uuid>',
      //   customer_id: '<uuid>',
      //   tax_id: '<uuid>',
      //   tax_note: 'Note related to the tax override',
      //   deleted_at: 'timestamp | null',
      //   version: '<number>'
      // }
      db.createObjectStore('customerTaxes', { keyPath: 'customer_id' })

    // eslint-disable-next-line no-fallthrough
    case 17:
      db.deleteObjectStore('brands')
      db.deleteObjectStore('suppliers')
      db.deleteObjectStore('tags')
      db.deleteObjectStore('productTypes')

    // IMPORTANT: If you intend to update the database schema please do so in a dedicated branch. This is because
    // a branch that updates the database schema can't be reverted easily.
  }
}

let dbPromise: Promise<IDBPDatabase<WebRegisterDb>>

export function open() {
  if (!dbPromise) {
    dbPromise = openDB('webregister', 18, {
      upgrade(db, oldVersion, newVersion, tx) {
        return upgradeDb(db, oldVersion, tx)
      },
      blocking() {
        // This should only happen during Cypress tests.
        // If we don't close the connection, it blocks Cypress from deleting the database.
        dbPromise.then(db => db.close())
      },
    }).catch(error => {
      trackError('[Database] Failed to open IndexedDB', {
        data: { error },
      })
    }) as Promise<IDBPDatabase<WebRegisterDb>>
  }
  return dbPromise
}

export function drop(): IPromise<void> {
  return $q.resolve(
    dbPromise.then(db => {
      db.close()
      deleteDB('webregister')
    })
  )
}

export function store<StoreName extends StoreNames<WebRegisterDb>>(
  name: StoreName
): Store<StoreName> {
  return new Store(open(), name)
}

const Database = {
  drop,
  store,
}

export type DatabaseService = typeof Database

/**
 * Initialises, creates and sets up the idb-based wrapper around the IndexedDB database.
 */
angular
  .module('webregister.services')
  .factory('Database', (wrDebugTools: $TSFixMe) => {
    'ngInject'
    wrDebugTools.register('Database')
    return Database
  })
